import { useAuthStore } from '../store/auth';
import { updateApiClientToken } from './api-client';
import { getNeonJwtToken } from './neon-auth';

/**
 * One-time boot check for protected apps.
 *
 * Neon Auth access tokens expire after ~15 minutes, but the app persists
 * the last JWT in localStorage and treated "token exists" as
 * "authenticated". On a cold load the stale token caused a parallel burst
 * of 401s — and when the Neon session was also gone, a forced logout after
 * the refresh attempt failed.
 *
 * This asks the Neon SDK for the current session's JWT (a fresh token when
 * the underlying session is still alive) BEFORE the first request fires,
 * so the app either boots with a valid token or routes straight to /login
 * without firing a doomed burst. When the SDK is not configured (local
 * dev), the stored token is left untouched and the existing 401/refresh
 * flow applies as before.
 */
export async function bootstrapAuth(): Promise<void> {
  let token: string | null = null;
  try {
    token = await getNeonJwtToken();
  } catch {
    // SDK not configured (local dev) — keep the stored token; the existing
    // 401/refresh flow applies as before.
    return;
  }

  if (token) {
    updateApiClientToken(token);
    useAuthStore.getState().setTokens(token);
    return;
  }

  // Neon session is gone — drop the stale token so the protected route
  // redirects to /login instead of mounting a session that can only 401.
  useAuthStore.getState().logout();
}
